/**
 * The denial path and the refused-deep-link marker, split out of
 * `entity-scope-resolution.ts` (DV-3013 PR 4, spec §4).
 *
 * Both halves of one story: `denied()` refuses a target and remembers that it
 * did; `storedTargetUnlessRefused()` is what reads that memory back. Keeping
 * them together is what stops the Critical-1 loop from being re-introduced by
 * someone who has only read one of them.
 */
import {
  isIntendedTargetDenied,
  markIntendedTargetDenied,
  peekIntendedUrl,
  retireDeniedIntendedUrl,
} from './intended-navigation';
import { recordAccessDenialDiag } from '../util/access-denial-log';
import type { ScopeOutcome } from './entity-scope-outcome';

/**
 * Record the denial diagnostic, send the user to the error surface, and
 * report the outcome.
 *
 * The assign lives HERE, not in the callers, because spec §3.4 gives the step
 * navigation for every outcome that is not `ready` / `no-organizations` — and
 * a denial is the one case with more than one caller that could get it wrong.
 * `bootstrapAuth` held this line originally; `CallBack`/`Loader` (the second
 * entry point, DV-3012 §3.1) had no equivalent, so a denied deep link left
 * them on a permanent spinner. Duplicating the line into the second caller
 * would put the same knowledge in three places; owning it once makes §3.4
 * true for every caller, present and future.
 *
 * D16: the reason travels as a query param rather than router state. There is
 * no router at the auth bootstrap, and a param survives the full-page trip
 * where state would not.
 *
 * `/error` is declared `entity: 'none'`, so landing there re-enters
 * `resolveEntityScope` (via `bootstrapAuth` on the fresh document) and
 * returns `ready` without re-reading the still-stored `intendedUrl` — which
 * is what stops this assign from looping (BC-2).
 */
export const denied = (
  reason: 'entity-access' | 'unknown',
  entityUUID: string,
  { fromStore, error }: { fromStore: boolean; error?: unknown },
): ScopeOutcome => {
  // A target that came from the STORED intended URL is refused once and then
  // remembered as refused, because nothing else retires it: BC-2 keeps the URL
  // stored, every later bootstrap re-reads it, and `endSession` preserves it —
  // so without this marker the denial repeats on `/`, on any typed `/home`
  // URL, and on the next login, with no affordance that escapes it (a
  // single-org customer's error page offers only "Go to Home"). Marked for
  // BOTH reasons, not just 'entity-access': an 'unknown' denial loops just as
  // hard, and losing one deep link to a transient failure is strictly better
  // than a bricked session — the link is still resolvable by following it
  // again, which arrives as current-URL provenance and is never skipped.
  if (fromStore) markIntendedTargetDenied(entityUUID);
  // Carried forward from EntityScopeGuard, NOT retired: 27 of 28 prod denial
  // sessions in the 30 days to 2026-09-09 came from that component, i.e. from
  // this exact path. Retire once BC-8 has shipped and this count has sat at
  // rest for a full release cycle. See util/access-denial-log.ts.
  recordAccessDenialDiag('lastAccessDenial', 'access-denied-redirect', {
    source: 'resolveEntityScope',
    reason,
    entityUUID,
    ...(error === undefined ? {} : { error: String(error) }),
  });
  window.location.assign(`/error?reason=${reason}`);
  return { kind: 'denied', reason };
};

/**
 * The stored intended URL's `entityUUID`, or null once that link has been
 * refused (Critical-1 loop, DV-3012).
 *
 * The refused link is not merely skipped, it is RETIRED here, together with
 * its marker: leaving it stored would let the next `'land-intended'` switch
 * — the single-org auto-select in step 4, say — consume it and land the user
 * straight back on the target that was just refused.
 *
 * Never reached from `/error` itself: that route is `entity: 'none'` and
 * returns before any of this, which is what keeps BC-2's "Switch
 * Organization" CTA landing on the deep link.
 */
export function storedTargetUnlessRefused(): string | null {
  const stored = peekIntendedUrl()?.queryParams?.entityUUID ?? null;
  if (!stored) return null;
  if (!isIntendedTargetDenied(stored)) return stored;
  retireDeniedIntendedUrl();
  return null;
}
